import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ProductVariant } from "@/types/product-type";
import { AlertTriangle, ChevronDown, Minimize2, PackageX, X } from "lucide-react";
import { useState } from "react";

interface OutOfStockPanelProps {
    outOfStocks: ProductVariant[];
    onClear: () => void;
}

export function OutOfStockPanel({ outOfStocks, onClear }: OutOfStockPanelProps) {
    const [isMinimized, setIsMinimized] = useState(false);

    if (!outOfStocks || outOfStocks.length === 0) return null;

    if (isMinimized) {
        return (
            <div className="fixed bottom-4 left-4 z-50 animate-in slide-in-from-left-5 fade-in duration-300">
                <Button
                    onClick={() => setIsMinimized(false)}
                    className="h-10 w-10 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg p-0 relative border-2 border-white dark:border-zinc-900"
                >
                    <AlertTriangle className="h-5 w-5" />
                    <span className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-[10px] font-bold rounded-full shadow-sm">
                        {outOfStocks.length > 99 ? '99+' : outOfStocks.length}
                    </span>
                </Button>
            </div>
        );
    }

    return (
        <div className="fixed bottom-4 left-4 z-50 w-80 bg-white dark:bg-zinc-900 border border-red-200 dark:border-red-900 rounded-xl shadow-xl overflow-hidden animate-in slide-in-from-left-5 fade-in duration-300 flex flex-col">
            {/* Header */}
            <div className="bg-red-50 dark:bg-red-950/30 p-2 pl-3 flex items-center justify-between border-b border-red-100 dark:border-red-900/50 shrink-0">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-400 font-semibold selection:bg-red-200 dark:selection:bg-red-900">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="text-sm">Out of Stock ({outOfStocks.length})</span>
                </div>
                <div className="flex items-center gap-0.5">
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-700 hover:text-red-800 hover:bg-red-100 dark:hover:bg-red-900/50"
                        onClick={() => setIsMinimized(true)}
                        title="Minimize"
                    >
                        <Minimize2 className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-red-700 hover:text-red-800 hover:bg-red-100 dark:hover:bg-red-900/50"
                                title="Dismiss All"
                            >
                                <X className="w-4 h-4" />
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Dismiss all notifications?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This will clear all out-of-stock notifications. This action cannot be undone.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={onClear} className="bg-red-600 hover:bg-red-700">Dismiss All</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>

            {/* List */}
            <ScrollArea className="max-h-[60vh]">
                <div className="p-1 space-y-1">
                    {outOfStocks.map((variant, idx) => (
                        <div
                            key={`${variant.id}-${idx}`}
                            className="flex items-center gap-2 p-1.5 rounded-md bg-zinc-50 dark:bg-zinc-900/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors border border-zinc-100 dark:border-zinc-800 group"
                        >
                            <div className="p-1.5 bg-red-100 dark:bg-red-900/20 rounded text-red-600 dark:text-red-400 shrink-0">
                                <PackageX className="w-3.5 h-3.5" />
                            </div>
                            <div className="flex-1 min-w-0 grid gap-0.5">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="text-xs font-medium text-zinc-900 dark:text-zinc-100 truncate">
                                        {variant.productName || "Product"}
                                    </p>
                                    <span className="text-[10px] font-mono bg-zinc-200 dark:bg-zinc-800 px-1 py-0.5 rounded text-zinc-600 dark:text-zinc-400 shrink-0">
                                        {variant.sku}
                                    </span>
                                </div>
                                <p className="text-[11px] text-zinc-500 truncate group-hover:text-zinc-700 dark:group-hover:text-zinc-300">
                                    {variant.name}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </ScrollArea>
        </div>
    );
}